import { todayParis, dateValue } from "../utils/calendar.mjs";

function planDate(value) {
  if (
    typeof value === "string" &&
    /^\d{4}-\d{2}-\d{2}T/.test(value) &&
    !Number.isNaN(Date.parse(value))
  )
    return todayParis(new Date(value));
  return dateValue(value);
}
export const taskDueDate = (task) =>
  planDate(task.next_action_date || task.next_due || task.due_date_original);
export function getNextActionDate(client, state) {
  // An explicit API/user date is authoritative, including over obsolete tasks.
  if (client.next_action_date) return planDate(client.next_action_date);
  const dates = state.tasks
    .filter(
      (t) => t.clientId === client.id && !t.done && t.category !== "manager",
    )
    .map(taskDueDate)
    .filter(Boolean)
    .sort();
  return dates[0] || dateValue(client.nextAction);
}
export function isRecruitmentSignal(signal) {
  return (
    !!signal &&
    !["activité en baisse", "insatisfaction"].includes(signal.type) &&
    !!(
      signal.job ||
      signal.qty ||
      signal.type === "besoin identifié" ||
      signal.type === "recrutement"
    )
  );
}
export function signalPosts(signal) {
  return (
    Number(signal?.qty) ||
    Number.parseInt(signal?.job || signal?.content || "", 10) ||
    0
  );
}
export function latestSignal(client, state, date = todayParis()) {
  return (
    state.signals
      .filter(
        (s) =>
          s.clientId === client.id &&
          s.date <= date &&
          !["fermé", "clos", "annulé"].includes(s.status),
      )
      .sort(
        (a, b) =>
          b.date.localeCompare(a.date) ||
          String(b.importedAt || "").localeCompare(String(a.importedAt || "")),
      )[0] || null
  );
}
export function getLastInteraction(client, state, date = todayParis()) {
  const types = [
    "call_result",
    "brochure_sent",
    "quote_sent",
    "quote_signed",
    "email_sent",
    "appointment_completed",
  ];
  const interactions = state.events
    .filter(
      (e) =>
        e.clientId === client.id &&
        types.includes(e.type) &&
        todayParis(new Date(e.createdAt)) <= date,
    )
    .map((e) => ({ at: e.createdAt, label: e.label }));
  for (const r of state.results.filter(
    (r) =>
      r.clientId === client.id && todayParis(new Date(r.createdAt)) <= date,
  ))
    interactions.push({ at: r.createdAt, label: r.label });
  if (
    client.lastContactAt &&
    todayParis(new Date(client.lastContactAt)) <= date
  )
    interactions.push({
      at: client.lastContactAt,
      label: "Dernier contact renseigné",
    });
  return interactions.sort((a, b) => b.at.localeCompare(a.at))[0] || null;
}
export function importantNewSignal(client, state, date = todayParis()) {
  const scheduled = getNextActionDate(client, state);
  const interaction = getLastInteraction(client, state, date)?.at || "";
  const plannedAt =
    state.tasks
      .filter(
        (t) =>
          t.clientId === client.id && !t.done && taskDueDate(t) === scheduled,
      )
      .map((t) => t.createdAt || "")
      .sort()
      .at(-1) || "";
  const after = [interaction, plannedAt, client.next_action_set_at || ""]
    .sort()
    .at(-1);
  return (
    state.signals
      .filter((s) => {
        const age = (Date.parse(date) - Date.parse(s.date)) / 86400000;
        const detected = s.importedAt || s.createdAt || `${s.date}T00:00:00Z`;
        return (
          s.clientId === client.id &&
          s.status === "nouveau" &&
          age >= 0 &&
          age <= 2 &&
          (s.important === true ||
            s.urgent === true ||
            (isRecruitmentSignal(s) && signalPosts(s) > 0)) &&
          (!after || detected > after)
        );
      })
      .sort((a, b) =>
        String(b.importedAt || b.date).localeCompare(
          String(a.importedAt || a.date),
        ),
      )[0] || null
  );
}
export function absolutePriority(task, client, state, date, now = new Date()) {
  const due = task ? taskDueDate(task) : null;
  if (task && (!due || due > date)) return null;
  const clock = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Paris",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(now);
  const promised =
    task &&
    (task.promised ||
      task.engagementId ||
      task.category === "appointment" ||
      /rappeler|rappel promis|devis promis/i.test(task.title));
  if (
    task &&
    (task.category === "appointment" ||
      (promised && /rappel|rappeler/i.test(task.title)))
  ) {
    const nowDue =
      due < date ||
      (date === todayParis(now) && task.time && task.time <= clock);
    return {
      rank: nowDue ? 0 : 1,
      why: nowDue
        ? "Rendez-vous ou rappel promis arrivé à échéance"
        : "Rendez-vous ou rappel promis aujourd’hui",
    };
  }
  if (
    task &&
    (task.category === "engagement" ||
      task.engagementId ||
      state.engagements.some((e) => !e.done && e.taskId === task.id))
  )
    return {
      rank: /devis/i.test(task.title) ? 3 : 2,
      why: /devis/i.test(task.title)
        ? "Devis promis au client à remettre"
        : "Engagement client à tenir à cette échéance",
    };
  if (task && promised && /devis/i.test(task.title))
    return { rank: 3, why: "Devis promis au client à remettre" };
  if (task && (task.category === "follow" || task.quoteId))
    return { rank: 4, why: "Relance arrivée à échéance" };
  if (task && due < date)
    return { rank: 5, why: `Tâche commerciale en retard depuis le ${due}` };
  const urgent =
    client.urgent ||
    state.needs.some(
      (n) =>
        n.clientId === client.id &&
        !["signé", "perdu", "suspendu"].includes(n.status) &&
        /urgent|immédiat|moins de 7/i.test(n.urgency || ""),
    );
  if (urgent)
    return { rank: 6, why: "Urgence client : besoin prioritaire à traiter" };
  return null;
}
